import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection } from '../types.js';
import {
  badge,
  bilingual,
  description,
  hasTitle,
  image,
  ingredients,
  languageFactor,
  price,
  raisedPrice,
  sectionSubtitle,
  sectionTitle,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';
import { escapeHtml, discountPercent } from '../format.js';

/**
 * Poster-style sections: huge condensed headings, a ribbon subtitle, items in two columns with
 * big raised-cents prices and size pills, and each section's own cut-out picture beside it.
 */

const DECORATIONS = ['Large picture in the top corner', 'Picture in the bottom corner'];

function itemHeight(board: MenuBoardContent, item: MenuItemContent): number {
  const lf = languageFactor(board);
  const text = (1.8 + (board.settings.showDescriptions && (item.description || item.descriptionAr) ? 3.4 : 0)) * lf;
  const priceBlock = item.variants.length ? item.variants.length * 2.5 : 3.4;
  return Math.max(text, priceBlock) + 1.6;
}

function blockHeight(board: MenuBoardContent, section: MenuSection, items: MenuItemContent[], continued: boolean, wide: boolean): number {
  const lf = languageFactor(board);
  const head = hasTitle(section) ? (continued ? 4.5 : 7.5) * (board.settings.language === 'both' && section.titleAr ? 1.45 : 1) : 0;
  const withImage = Boolean(section.imageMediaId) && !continued;
  const cols = withImage || !wide ? 1 : 2;
  let body = 0;
  for (let i = 0; i < items.length; i += cols) {
    body += Math.max(...items.slice(i, i + cols).map((item) => itemHeight(board, item)));
  }
  if (withImage) body = Math.max(body, 15);
  return head + body * (lf > 1 ? 1.05 : 1) + 2;
}

function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const hasHero = Boolean(board.decorMediaIds[0]);
  // The corner picture sits at the foot of the first column; keep text clear of it.
  const corner = board.decorMediaIds[1] ? 14 : 0;
  const column = (capacity: number, wide: boolean): MenuColumn => ({
    capacity,
    blockCost: (section, items, continued) => blockHeight(board, section, items, continued, wide),
  });
  if (portrait) return [column((hasHero ? 82 : 113) - corner, true)];
  return [column(62 - corner, true), column(hasHero ? 35.5 : 62, true)];
}

function prices(ctx: RenderContext, item: MenuItemContent): string {
  if (item.variants.length) {
    return `<div class="bs-vars">${item.variants
      .map(
        (v) =>
          `<div class="bs-var"><span class="bs-pill">${variantLabel(ctx, v.label, v.labelAr)}</span>` +
          (v.detail ? `<span class="bs-detail" dir="auto">${escapeHtml(v.detail)}</span>` : '') +
          raisedPrice(ctx, v.price, 'bs-rp') +
          '</div>',
      )
      .join('')}</div>`;
  }
  const selling = discountPercent(item) !== null ? item.discountPrice! : item.price;
  return [
    '<div class="bs-pr">',
    raisedPrice(ctx, selling, 'bs-rp'),
    discountPercent(item) !== null ? `<s class="bs-old" dir="ltr">${price(ctx, item.price)}</s>` : '',
    item.calories != null ? `<span class="bs-cal" dir="ltr">${item.calories} CAL</span>` : '',
    '</div>',
  ].join('');
}

function block(ctx: RenderContext, b: MenuBlock): string {
  const withImage = Boolean(b.section.imageMediaId) && !b.continued;
  const picture = withImage ? image(ctx, b.section.imageMediaId, 'bs-sec-img') : '';
  const head = hasTitle(b.section)
    ? `<div class="bs-head${b.continued ? ' bs-cont' : ''}">${sectionTitle(ctx, b.section, 'bs-title')}${
        b.continued ? '' : sectionSubtitle(ctx, b.section, 'bs-ribbon')
      }</div>`
    : '';
  const items = b.items
    .map((item) => {
      const badges = badge(ctx, item, 'bs-badge');
      return [
        '<div class="bs-item">',
        '<div class="bs-it-text">',
        bilingual(ctx, item.title, item.titleAr, 'bs-name'),
        description(ctx, item, 'bs-desc'),
        ingredients(ctx, item, 'bs-ingr'),
        badges ? `<div class="bs-badges">${badges}</div>` : '',
        '</div>',
        prices(ctx, item),
        '</div>',
      ].join('');
    })
    .join('');
  return `<div class="bs-sec">${head}<div class="bs-body${picture ? ' bs-with-img' : ''}"><div class="bs-items">${items}</div>${picture}</div></div>`;
}

export const boldSections: MenuTemplate = {
  id: 'bold-sections',
  name: 'Bold sections',
  description:
    'Big poster headings for each section, two columns of items with large prices and size pills, and a picture beside each section.',
  defaultTheme: {
    backgroundColor: '#f6f2ea',
    surfaceColor: '#ffffff',
    textColor: '#3b3632',
    primaryColor: '#3e2a1f',
    accentColor: '#11827f',
    fontId: 'street',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'teal', name: 'Teal', theme: {} },
    { id: 'tomato', name: 'Tomato', theme: { accentColor: '#d62828', primaryColor: '#2b2118' } },
    { id: 'mustard', name: 'Mustard', theme: { accentColor: '#c98a06', primaryColor: '#2b2118', backgroundColor: '#fbf6ea' } },
    { id: 'night', name: 'Night', theme: { backgroundColor: '#141414', surfaceColor: '#1f1f1f', textColor: '#e9e9e9', primaryColor: '#ffffff', accentColor: '#2ec4b6' } },
  ],
  cuisines: ['burgers', 'pizza'],
  uses: { featured: false, sectionImages: true, brand: false, decorations: DECORATIONS },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const hero = image(ctx, ctx.board.decorMediaIds[0], 'bs-hero');
    const corner = image(ctx, ctx.board.decorMediaIds[1], 'bs-corner');
    const cols = ctx.page.columns
      .map(
        (column, index) =>
          `<div class="bs-col${index === ctx.page.columns.length - 1 && hero ? ' bs-col-under-hero' : ''}">${column
            .map((b) => block(ctx, b))
            .join('')}</div>`,
      )
      .join('');
    return `${hero}${corner}<div class="bs-cols">${cols}</div>`;
  },

  css: `
.mb-t-bold-sections{padding:2.4em 3em;color:var(--mb-text);
  background-image:radial-gradient(circle at 20% 20%,rgba(255,255,255,.55),transparent 55%),radial-gradient(circle at 90% 85%,rgba(0,0,0,.05),transparent 50%)}
.mb-t-bold-sections .bs-cols{position:relative;z-index:1;flex:1;min-height:0;display:grid;grid-template-columns:1fr 1fr;gap:4em}
.mb-t-bold-sections.mb-portrait .bs-cols{grid-template-columns:1fr}
.mb-t-bold-sections .bs-col{min-width:0;min-height:0;display:flex;flex-direction:column;gap:2em}
.mb-t-bold-sections .bs-col-under-hero{padding-top:28.5em}
.mb-t-bold-sections.mb-portrait .bs-col-under-hero{padding-top:33em}
.mb-t-bold-sections .bs-hero{position:absolute;z-index:0;top:-6%;right:-5%;width:50%;height:46%;object-fit:cover;
  border-radius:1.4em;transform:rotate(-4deg);box-shadow:0 1.5em 3em rgba(0,0,0,.25)}
.mb-t-bold-sections.mb-portrait .bs-hero{width:90%;height:30%;right:-12%}
.mb-t-bold-sections[dir=rtl] .bs-hero{right:auto;left:-6%;transform:rotate(4deg)}
.mb-t-bold-sections .bs-corner{position:absolute;z-index:0;bottom:-5%;left:-2%;width:17em;height:17em;object-fit:cover;
  border-radius:50%;box-shadow:0 1em 2em rgba(0,0,0,.25)}
.mb-t-bold-sections[dir=rtl] .bs-corner{left:auto;right:-2%}
.mb-t-bold-sections .bs-head{display:flex;align-items:flex-end;flex-wrap:wrap;gap:.3em 1.2em;margin-bottom:1em}
.mb-t-bold-sections .bs-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:6.5em;
  line-height:.85;text-transform:uppercase;color:var(--mb-primary);letter-spacing:.01em}
.mb-t-bold-sections .bs-cont .bs-title{font-size:3.6em;opacity:.8}
.mb-t-bold-sections .bs-title.mb-2nd{font-size:3.4em;flex-basis:100%}
.mb-t-bold-sections .bs-ribbon{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.4em;
  text-transform:uppercase;background:var(--mb-accent);color:var(--mb-surface);padding:.2em .8em;transform:skewX(-8deg) translateY(-.4em);
  letter-spacing:.04em;align-self:flex-start}
.mb-t-bold-sections .bs-ribbon.mb-2nd{transform:skewX(8deg)}
.mb-t-bold-sections .bs-body{display:flex;gap:2em;align-items:flex-start}
.mb-t-bold-sections .bs-items{flex:1;min-width:0;display:grid;grid-template-columns:1fr 1fr;gap:1.6em 2.6em}
.mb-t-bold-sections .bs-with-img .bs-items{grid-template-columns:1fr}
.mb-t-bold-sections .bs-sec-img{flex:0 0 38%;aspect-ratio:4/3;max-height:16em;object-fit:cover;border-radius:1em;
  transform:rotate(2deg);box-shadow:0 1em 2em rgba(0,0,0,.22)}
.mb-t-bold-sections .bs-item{display:flex;gap:1em;align-items:flex-start}
.mb-t-bold-sections .bs-it-text{flex:1;min-width:0}
.mb-t-bold-sections .bs-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.75em;
  text-transform:uppercase;color:var(--mb-primary);line-height:1}
.mb-t-bold-sections .bs-name.mb-2nd{font-size:1.35em;margin-top:.1em}
.mb-t-bold-sections .bs-desc{font-size:.88em;line-height:1.3;margin-top:.3em;
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.mb-t-bold-sections .bs-ingr{font-size:.75em;opacity:.7;margin-top:.2em}
.mb-t-bold-sections .bs-badges{display:flex;gap:.4em;margin-top:.4em}
.mb-t-bold-sections .bs-badge{font-family:var(--mb-font-heading);font-size:.95em;text-transform:uppercase;background:var(--mb-accent);
  color:var(--mb-surface);padding:.1em .55em;border-radius:.2em}
.mb-t-bold-sections .bs-pr{display:flex;flex-direction:column;align-items:flex-end;flex-shrink:0}
.mb-t-bold-sections .bs-rp{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);
  color:var(--mb-accent);white-space:nowrap;line-height:.9}
.mb-t-bold-sections .bs-rp-w{font-size:3em}
.mb-t-bold-sections .bs-rp-f{font-size:1.3em;vertical-align:top;position:relative;top:.15em;margin-inline-start:.05em}
.mb-t-bold-sections .bs-rp-s{font-size:1.1em;margin:0 .15em;opacity:.85}
.mb-t-bold-sections .bs-old{opacity:.55;font-size:1.05em;font-weight:600;margin-top:.15em;white-space:nowrap}
.mb-t-bold-sections .bs-cal{font-size:.72em;font-weight:600;letter-spacing:.05em;opacity:.7;margin-top:.3em}
.mb-t-bold-sections .bs-vars{display:flex;flex-direction:column;gap:.4em;flex-shrink:0}
.mb-t-bold-sections .bs-var{display:flex;align-items:center;gap:.6em;justify-content:flex-end}
.mb-t-bold-sections .bs-var .bs-rp-w{font-size:2.3em}
.mb-t-bold-sections .bs-pill{font-family:var(--mb-font-heading);font-size:1em;text-transform:uppercase;background:var(--mb-accent);
  color:var(--mb-surface);padding:.1em .6em;border-radius:.2em;letter-spacing:.04em}
.mb-t-bold-sections .bs-detail{font-size:.72em;font-weight:600;opacity:.75;text-transform:uppercase}
`,
};
